import { Card, Divider, Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormButtonOptions from '../../../FormFields/FormButtonOptions';
import FormDatePickerField from '../../../FormFields/FormDatePickerField';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormPhoneNumberField from '../../../FormFields/FormPhoneNumField';
import FormTextField from '../../../FormFields/FormTextField';
import FormNumberField from '../../../FormFields/FormNumberField';
import FormTitle from '../../../FormFields/FormTitle';
import FormVendorField from '../../../FormFields/FormVendorField';
import { formCardStyle, groupedFieldsStyle } from '../../../TransitionStyles';
import { validateAddress, validateEmail } from '../../../ValidateFunctions';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { DIETARY } from '../../../formQuestionsEnum';
import { OUTSOURCED_COMPANY_CATEGORIES, OWNERSHIP_TYPES } from '../../../optionEnums';

DietaryDepartmentForm.propTypes = {
  permissions: PropTypes.object,
};

export default function DietaryDepartmentForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);
  const {
    data: { outsourcedCompanyCategoriesList, ownershipTypesList },
  } = useSelector((state) => state.transitions);
  const { watch } = useFormContext();

  const IsOutsourced = watch(DIETARY.DIETARY_IS_OUTSOURCED.fieldName);
  const dishMachineOwnershipType = watch(DIETARY.DISH_MACHINE_OWNERSHIP_TYPE.fieldName);
  const dishMachineIsFinanced = watch(DIETARY.DISH_MACHINE_IS_FINANCED.fieldName);

  const fieldIds = [
    DIETARY.DIETARY_IS_OUTSOURCED.id,
    DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_CATEGORY.id,
    DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_VENDOR.id,
    DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_PHONE.id,
    DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_EMAIL.id,
    DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_HOURS_PER_WEEK.id,
    DIETARY.MAIN_FOOD_SUPPLIER.id,
    DIETARY.SECONDARY_FOOD_SUPPLIER.id,
    DIETARY.DAIRY_PROVIDER.id,
    DIETARY.BAKERY_PROVIDER.id,
    DIETARY.CHEMICAL_PROVIDER.id,
    DIETARY.PAPER_PLASTIC_VENDOR.id,
    DIETARY.COFFEE_VENDOR.id,
    DIETARY.DISH_MACHINE_OWNERSHIP_TYPE.id,
    DIETARY.DISH_MACHINE_SERIAL.id,
    DIETARY.DISH_MACHINE_MANUFACTURER.id,
    DIETARY.DISH_MACHINE_MODEL_NUMBER.id,
    DIETARY.DISH_MACHINE_PURCHASED_DATE.id,
    DIETARY.DISH_MACHINE_IS_FINANCED.id,
    DIETARY.DISH_MACHINE_FINANCE_AGREEMENT.id,
    DIETARY.DISH_MACHINE_LEASED_COMPANY_NAME.id,
    DIETARY.DISH_MACHINE_LEASED_PHONE_NUMBER.id,
    DIETARY.DISH_MACHINE_LEASED_EMAIL.id,
    DIETARY.DISH_MACHINE_LEASED_COMPANY_ADDRESS.id,
    DIETARY.DISH_MACHINE_LEASED_AGREEMENT.id,
    DIETARY.DISH_MACHINE_RENTAL_COMPANY_NAME.id,
    DIETARY.DISH_MACHINE_RENTAL_PHONE_NUMBER.id,
    DIETARY.DISH_MACHINE_RENTAL_EMAIL.id,
    DIETARY.DISH_MACHINE_RENTAL_COMPANY_ADDRESS.id,
    DIETARY.DISH_MACHINE_RENTAL_CONTRACT.id,
  ];

  const showForm = fieldIds.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Dietary department
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {DIETARY.DIETARY_IS_OUTSOURCED.id in permissions && (
            <Grid item xs={12} lg={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Department type' }} />
                <FormButtonOptions
                  fieldName={DIETARY.DIETARY_IS_OUTSOURCED.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[DIETARY.DIETARY_IS_OUTSOURCED.id],
                    options: [
                      { label: 'Outsourced', value: true },
                      { label: 'In House', value: false },
                    ],
                  }}
                />
              </FieldContainer>
            </Grid>
          )}

          {IsOutsourced === true && (
            <>
              {DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_CATEGORY.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Choose Category' }} />
                    <FormButtonOptions
                      fieldName={DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_CATEGORY.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_CATEGORY.id],
                        options: outsourcedCompanyCategoriesList.filter(
                          (x) =>
                            x.value === OUTSOURCED_COMPANY_CATEGORIES.FULL_DEPARTMENT ||
                            x.value === OUTSOURCED_COMPANY_CATEGORIES.MANAGER_ONLY
                        ),
                      }}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_VENDOR.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle
                    fieldAttributes={{
                      questionTitle: 'Dietary outsourced company',
                      fieldType: 'title',
                    }}
                  />
                  <FormVendorField
                    fieldName={DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_VENDOR.fieldName}
                    fieldAttributes={{ isEditable: permissions[DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_VENDOR.id] }}
                    invoiceFileTypeId={FILE_TYPES.DIETARY_OUTSOURCED_COMPANY_INVOICE}
                    contractFileTypeId={FILE_TYPES.DIETARY_OUTSOURCED_COMPANY_CONTRACT}
                  />
                </Grid>
              )}

              {DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_PHONE.id in permissions && (
                <Grid item xs={12} md={5}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Phone' }} />
                    <FormPhoneNumberField
                      fieldName={DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_PHONE.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_PHONE.id] }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_EMAIL.id in permissions && (
                <Grid item xs={12} md={5}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Email' }} />
                    <FormTextField
                      fieldName={DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_EMAIL.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_EMAIL.id],
                        validation: {
                          pattern: { ...validateEmail },
                        },
                      }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_HOURS_PER_WEEK.id in permissions && (
                <Grid item xs={12} md={2}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Hours per week' }} />
                    <FormNumberField
                      fieldName={DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_HOURS_PER_WEEK.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[DIETARY.DIETARY_DEPARTMENT_OUTSOURCED_HOURS_PER_WEEK.id],
                      }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                      allowDecimal
                    />
                  </FieldContainer>
                </Grid>
              )}
            </>
          )}
          {IsOutsourced === false && (
            <>
              {DIETARY.MAIN_FOOD_SUPPLIER.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Main food supplier', fieldType: 'title' }} />
                    <FormVendorField
                      fieldName={DIETARY.MAIN_FOOD_SUPPLIER.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.MAIN_FOOD_SUPPLIER.id] }}
                      invoiceFileTypeId={FILE_TYPES.MAIN_FOOD_SUPPLIER_INVOICE}
                      contractFileTypeId={FILE_TYPES.MAIN_FOOD_SUPPLIER_CONTRACT}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.SECONDARY_FOOD_SUPPLIER.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Secondary food supplier', fieldType: 'title' }} />
                    <FormVendorField
                      fieldName={DIETARY.SECONDARY_FOOD_SUPPLIER.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.SECONDARY_FOOD_SUPPLIER.id] }}
                      invoiceFileTypeId={FILE_TYPES.SECONDARY_FOOD_SUPPLIER_INVOICE}
                      contractFileTypeId={FILE_TYPES.SECONDARY_FOOD_SUPPLIER_CONTRACT}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.DAIRY_PROVIDER.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Dairy provider', fieldType: 'title' }} />

                    <FormVendorField
                      fieldName={DIETARY.DAIRY_PROVIDER.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.DAIRY_PROVIDER.id] }}
                      invoiceFileTypeId={FILE_TYPES.DAIRY_PROVIDER_INVOICE}
                      contractFileTypeId={FILE_TYPES.DAIRY_PROVIDER_CONTRACT}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.BAKERY_PROVIDER.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Bakery provider', fieldType: 'title' }} />

                    <FormVendorField
                      fieldName={DIETARY.BAKERY_PROVIDER.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.BAKERY_PROVIDER.id] }}
                      invoiceFileTypeId={FILE_TYPES.BAKERY_PROVIDER_INVOICE}
                      contractFileTypeId={FILE_TYPES.BAKERY_PROVIDER_CONTRACT}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.CHEMICAL_PROVIDER.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Chemical provider', fieldType: 'title' }} />
                    <FormVendorField
                      fieldName={DIETARY.CHEMICAL_PROVIDER.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.CHEMICAL_PROVIDER.id] }}
                      invoiceFileTypeId={FILE_TYPES.CHEMICAL_PROVIDER_INVOICE}
                      contractFileTypeId={FILE_TYPES.CHEMICAL_PROVIDER_CONTRACT}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.PAPER_PLASTIC_VENDOR.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Paper/Plastic vendor', fieldType: 'title' }} />
                    <FormVendorField
                      fieldName={DIETARY.PAPER_PLASTIC_VENDOR.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.PAPER_PLASTIC_VENDOR.id] }}
                      invoiceFileTypeId={FILE_TYPES.PAPER_PLASTIC_VENDOR_INVOICE}
                      contractFileTypeId={FILE_TYPES.PAPER_PLASTIC_VENDOR_CONTRACT}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.COFFEE_VENDOR.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Coffee vendor', fieldType: 'title' }} />
                    <FormVendorField
                      fieldName={DIETARY.COFFEE_VENDOR.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.COFFEE_VENDOR.id] }}
                      invoiceFileTypeId={FILE_TYPES.COFFEE_VENDOR_INVOICE}
                      contractFileTypeId={FILE_TYPES.COFFEE_VENDOR_CONTRACT}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.DISH_MACHINE_OWNERSHIP_TYPE.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Dish machine', fieldType: 'title' }} />
                    <FormButtonOptions
                      fieldName={DIETARY.DISH_MACHINE_OWNERSHIP_TYPE.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[DIETARY.DISH_MACHINE_OWNERSHIP_TYPE.id],
                        options: ownershipTypesList.filter(
                          (x) =>
                            x.value === OWNERSHIP_TYPES.OWNED ||
                            x.value === OWNERSHIP_TYPES.LEASED ||
                            x.value === OWNERSHIP_TYPES.RENTAL
                        ),
                      }}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {String(dishMachineOwnershipType) === OWNERSHIP_TYPES.OWNED && (
                <Grid container spacing={1} sx={{ mt: 1, ml: 1, ...groupedFieldsStyle, width: '100%' }}>
                  {DIETARY.DISH_MACHINE_SERIAL.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Serial Number' }} />
                        <FormTextField
                          fieldName={DIETARY.DISH_MACHINE_SERIAL.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_SERIAL.id],
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_MANUFACTURER.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Manufacturer' }} />
                        <FormTextField
                          fieldName={DIETARY.DISH_MACHINE_MANUFACTURER.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_MANUFACTURER.id],
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_MODEL_NUMBER.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Model number' }} />
                        <FormTextField
                          fieldName={DIETARY.DISH_MACHINE_MODEL_NUMBER.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_MODEL_NUMBER.id],
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_PURCHASED_DATE.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Date purchased' }} />
                        <FormDatePickerField
                          fieldName={DIETARY.DISH_MACHINE_PURCHASED_DATE.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_PURCHASED_DATE.id],
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                          maxDate={dayjs().startOf('day').toDate()}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_IS_FINANCED.id in permissions && (
                    <Grid item xs={12}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Dish machine' }} />
                        <FormButtonOptions
                          fieldName={DIETARY.DISH_MACHINE_IS_FINANCED.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_IS_FINANCED.id],
                            options: [
                              { label: 'Purchased', value: false },
                              { label: 'Financed', value: true },
                            ],
                          }}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {dishMachineIsFinanced === true && (
                    <>
                      {DIETARY.DISH_MACHINE_FINANCE_AGREEMENT.id in permissions && (
                        <Grid item xs={12}>
                          <FieldContainer>
                            <FormTitle
                              fieldAttributes={{ questionTitle: 'Upload finance agreement', fieldType: 'file' }}
                            />
                            <FormFileDrop
                              fieldName={DIETARY.DISH_MACHINE_FINANCE_AGREEMENT.fieldName}
                              fieldAttributes={{
                                isEditable: permissions[DIETARY.DISH_MACHINE_FINANCE_AGREEMENT.id],
                                fileTypeId: FILE_TYPES.DISH_MACHINE_FINANCE_AGREEMENT,
                              }}
                            />
                          </FieldContainer>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              )}

              {String(dishMachineOwnershipType) === OWNERSHIP_TYPES.LEASED && (
                <Grid container spacing={1} sx={{ mt: 1, ml: 1, ...groupedFieldsStyle, width: '100%' }}>
                  {DIETARY.DISH_MACHINE_LEASED_COMPANY_NAME.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Company name' }} />
                        <FormTextField
                          fieldName={DIETARY.DISH_MACHINE_LEASED_COMPANY_NAME.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_LEASED_COMPANY_NAME.id],
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_LEASED_PHONE_NUMBER.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Phone number' }} />
                        <FormPhoneNumberField
                          fieldName={DIETARY.DISH_MACHINE_LEASED_PHONE_NUMBER.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_LEASED_PHONE_NUMBER.id],
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_LEASED_EMAIL.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Email address' }} />
                        <FormTextField
                          fieldName={DIETARY.DISH_MACHINE_LEASED_EMAIL.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_LEASED_EMAIL.id],
                            validation: {
                              pattern: { ...validateEmail },
                            },
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_LEASED_COMPANY_ADDRESS.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Company address' }} />
                        <FormTextField
                          fieldName={DIETARY.DISH_MACHINE_LEASED_COMPANY_ADDRESS.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_LEASED_COMPANY_ADDRESS.id],
                            validation: {
                              validate: validateAddress,
                            },
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_LEASED_AGREEMENT.id in permissions && (
                    <Grid item xs={12}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Upload lease agreement', fieldType: 'file' }} />
                        <FormFileDrop
                          fieldName={DIETARY.DISH_MACHINE_LEASED_AGREEMENT.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_LEASED_AGREEMENT.id],
                            fileTypeId: FILE_TYPES.DISH_MACHINE_LEASE_AGREEMENT,
                          }}
                        />
                      </FieldContainer>
                    </Grid>
                  )}
                </Grid>
              )}

              {String(dishMachineOwnershipType) === OWNERSHIP_TYPES.RENTAL && (
                <Grid container spacing={1} sx={{ mt: 1, ml: 1, ...groupedFieldsStyle, width: '100%' }}>
                  {DIETARY.DISH_MACHINE_RENTAL_COMPANY_NAME.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Company name' }} />
                        <FormTextField
                          fieldName={DIETARY.DISH_MACHINE_RENTAL_COMPANY_NAME.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_RENTAL_COMPANY_NAME.id],
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_RENTAL_PHONE_NUMBER.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Phone number' }} />
                        <FormPhoneNumberField
                          fieldName={DIETARY.DISH_MACHINE_RENTAL_PHONE_NUMBER.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_RENTAL_PHONE_NUMBER.id],
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_RENTAL_EMAIL.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Email address' }} />
                        <FormTextField
                          fieldName={DIETARY.DISH_MACHINE_RENTAL_EMAIL.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_RENTAL_EMAIL.id],
                            validation: {
                              pattern: { ...validateEmail },
                            },
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_RENTAL_COMPANY_ADDRESS.id in permissions && (
                    <Grid item xs={12} lg={6}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Company address' }} />
                        <FormTextField
                          fieldName={DIETARY.DISH_MACHINE_RENTAL_COMPANY_ADDRESS.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_RENTAL_COMPANY_ADDRESS.id],
                            validation: {
                              validate: validateAddress,
                            },
                          }}
                          focusedField={focusedField}
                          setFocusedField={setFocusedField}
                        />
                      </FieldContainer>
                    </Grid>
                  )}

                  {DIETARY.DISH_MACHINE_RENTAL_CONTRACT.id in permissions && (
                    <Grid item xs={12}>
                      <FieldContainer>
                        <FormTitle fieldAttributes={{ questionTitle: 'Upload rental contract', fieldType: 'file' }} />
                        <FormFileDrop
                          fieldName={DIETARY.DISH_MACHINE_RENTAL_CONTRACT.fieldName}
                          fieldAttributes={{
                            isEditable: permissions[DIETARY.DISH_MACHINE_RENTAL_CONTRACT.id],
                            fileTypeId: FILE_TYPES.DISH_MACHINE_RENTAL_CONTRACT,
                          }}
                        />
                      </FieldContainer>
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Card>
    )
  );
}
